/**
 * GraphListToggle — a small Graph/List/Elaboració switch for wide screens.
 *
 * Narrow screens already switch between all three (plus a fourth, unrelated
 * one) via TabSelector (see TabSelector.ts). On wide screens this used to
 * be a genuine Graph-vs-List-only toggle, with Elaboració always visible
 * below both — it's now a real third option here too, so wide screens
 * match narrow ones in always showing exactly one of the three at a time,
 * never more.
 *
 * Shared between the home page and the dedicated recipe page — both wire
 * it to show/hide their own wide-screen graph/list/elaboració containers.
 */

export type GraphListView = 'graph' | 'list' | 'elaboracio' | 'timeline';

interface ViewDef {
  id: GraphListView;
  label: string;
  icon: string;
}

// Same icons, same order as TabSelector's ALL_TABS — the two toggles
// should read as one family (narrow vs wide screen) rather than two
// independent designs.
const DEFS: ViewDef[] = [
  { id: 'graph', label: 'Graf', icon: '⬡' },
  { id: 'list', label: 'Llista', icon: '≡' },
  { id: 'elaboracio', label: 'Elaboració', icon: '⚗' },
  { id: 'timeline', label: 'Cronologia', icon: '◷' },
];

export class GraphListToggle {
  private container: HTMLElement;
  private active: GraphListView;
  private buttons: Map<GraphListView, HTMLButtonElement> = new Map();
  private onChangeCallback: ((view: GraphListView) => void) | null = null;

  constructor(container: HTMLElement, initial: GraphListView = 'graph') {
    this.container = container;
    this.active = initial;
    this.render();
  }

  private render(): void {
    this.container.innerHTML = '';
    this.buttons.clear();

    for (const d of DEFS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.title = d.label;
      btn.setAttribute('aria-label', d.label);
      btn.addEventListener('click', () => this.setActive(d.id));
      this.buttons.set(d.id, btn);
      this.container.appendChild(btn);
    }
    this.refreshButtons();
  }

  /**
   * Icon-only when inactive, icon + label when active — same convention
   * NodeVisualizer's own edit-panel tabs use (see renderEditableContent's
   * activateTab()), and the same one TabSelector now uses for its narrow-
   * screen counterpart of these same four views.
   */
  private refreshButtons(): void {
    for (const d of DEFS) {
      const btn = this.buttons.get(d.id);
      if (!btn) continue;
      btn.className = this.buttonClass(d.id);
      btn.innerHTML = d.id === this.active
        ? `${d.icon} <span class="text-sm font-medium">${d.label}</span>`
        : d.icon;
    }
  }

  /**
   * Matches TabSelector's button styling so the two toggles read as one
   * family. Always includes the stable `graph-list-toggle-btn` class, same
   * reason as TabSelector's `tab-selector-btn` — GraphVisualizer's
   * pick-node outside-click handler hooks into it. Underline style matches
   * NodeVisualizer's edit-panel tabs (border-b-2, text-lg icon).
   */
  private buttonClass(id: GraphListView): string {
    const base = 'graph-list-toggle-btn px-3 py-1.5 text-lg leading-none border-b-2 transition-colors whitespace-nowrap';
    if (id === this.active) {
      return `${base} border-[var(--accent)] text-[var(--accent)]`;
    }
    return `${base} border-transparent text-[#888] hover:text-[var(--accent)]`;
  }

  /** Set the active view and re-render button styles. Fires onChange if it actually changed. */
  setActive(view: GraphListView): void {
    if (view === this.active) return;
    this.active = view;
    this.refreshButtons();
    this.onChangeCallback?.(view);
  }

  getActive(): GraphListView {
    return this.active;
  }

  /** Register a callback fired when the user switches views (not fired on initial render). */
  onChange(callback: (view: GraphListView) => void): void {
    this.onChangeCallback = callback;
  }
}
